import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SnapList, SnapItem, useScroll, useDragToScroll } from 'react-snaplist-carousel';
import { TestimonialVideo } from '../types';
import { TestimonialCard } from './testimonial-card';
import { CustomVideoPlayerRef } from './custom-video-player';
import { useCarouselPadding } from '../../shared/hooks/use-carousel-padding';
import {
  CARD_GAP,
  INTERSECTION_THRESHOLD,
} from '../constants';

interface TestimonialsCarouselProps {
  testimonials: TestimonialVideo[];
}

export const TestimonialsCarousel: React.FC<TestimonialsCarouselProps> = ({ testimonials }) => {
  const { t } = useTranslation();
  const snapListRef = useRef<HTMLDivElement>(null);
  const [currentlyPlayingIndex, setCurrentlyPlayingIndex] = useState<number | null>(null);

  useScroll({ ref: snapListRef });
  useDragToScroll({ ref: snapListRef });

  const videoRefs = useRef<Array<CustomVideoPlayerRef | null>>([]);

  const paddingHorizontal = useCarouselPadding();

  const createRefCallback = useCallback(
    (index: number): React.Ref<CustomVideoPlayerRef> => {
      return (el: CustomVideoPlayerRef | null) => {
        videoRefs.current[index] = el;
      };
    },
    []
  );

  const handleVideoPlay = (playingIndex: number) => {
    videoRefs.current.forEach((ref, index) => {
      if (index !== playingIndex && ref && ref.isPlaying) {
        ref.pause();
      }
    });

    setCurrentlyPlayingIndex(playingIndex);
  };

  const handleVideoPause = () => {
    setCurrentlyPlayingIndex(null);
  };

  // Auto-pause videos when scrolled out of view
  useEffect(() => {
    const cards = snapListRef.current?.querySelectorAll('[data-video-card]');
    if (!cards || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt(entry.target.getAttribute('data-video-index') || '0', 10);
          const videoRef = videoRefs.current[index];
          if (!entry.isIntersecting && videoRef?.isPlaying) {
            videoRef.pause();
          }
        });
      },
      {
        threshold: INTERSECTION_THRESHOLD,
        rootMargin: '0px'
      }
    );

    cards.forEach((card) => observer.observe(card));

    return () => {
      observer.disconnect();
    };
  }, []);

  if (testimonials.length === 0) {
    return (
      <div className="mb-7 mt-10 flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-text-secondary">{t('js.features.marketplace.home.what_our_housemates_say_section.no_testimonials_available')}</p>
        </div>
      </div>
    );
  }

  return (
    <SnapList
      ref={snapListRef as any}
      direction="horizontal"
      className="pb-6 pt-2"
    >
      {testimonials.map((video, index) => (
        <SnapItem
          key={video.id}
          margin={{
            left: index === 0 ? `${paddingHorizontal}px` : `${CARD_GAP}px`,
            right: index === testimonials.length - 1 ? `${paddingHorizontal}px` : `${CARD_GAP}px`,
          }}
          snapAlign="center"
        >
          <div
            data-video-card
            data-video-index={index}
          >
            <TestimonialCard
              ref={createRefCallback(index) as any}
              video={video}
              onPlay={() => handleVideoPlay(index)}
              onPause={handleVideoPause}
              isPlaying={currentlyPlayingIndex === index}
            />
          </div>
        </SnapItem>
      ))}
    </SnapList>
  );
};
